/**
 * 개인정보 가리기 — **평소엔 안 가리고, 필요할 때 켠다. 켠 뒤 푸는 건 매장 암호**(티켓 #459·#461).
 *
 * 가리는 규칙(무엇을 어떻게 가리나)은 shared/privacy-mask.ts 가 진본이고, 이 파일은
 * "지금 가려져 있나 / 언제 다시 가려지나"만 들고 있다. 화면은 useMaskedValue / MaskedText 로
 * 값을 통과시키고, 막히는 동작(문자·전화·인쇄·내보내기)은 useMaskedActionGuard 로 막는다.
 *
 * ⚠️ **평소 상태가 두 번 뒤집혔다** — 처음엔 브라우저에 켜짐/꺼짐을 저장하는 스위치였고,
 *    2026-08-13(#461)에 "언제나 가린 채로 시작"으로 갔다가, 2026-08-14(#459)에 지금의
 *    **"평소엔 안 가림"** 으로 돌아왔다. 늘 가려두니 사진 인쇄·문자·서식 출력이 죄다
 *    `준비중입니다` 로 막혀 **기능 자체가 없는 것처럼 보였다**(신분증 출력이 그렇게 반려됐다).
 *    가리기를 당분간 안 쓰기로 한 결정이라, 되살릴 땐 그 결정부터 확인한다.
 *
 * ⚠️ **상태는 아무 데도 저장하지 않는다(이 탭의 메모리에만).** 켜 뒀더라도 새 탭·새로고침이면
 *    안 가린 상태로 돌아온다. 저장하면 "껐다 켜도 가려져 있는" 상태가 되어, 아무도 못 푸는
 *    화면에 갇히는 사고가 난다.
 *
 * ⚠️ 이건 화면 가리개이지 접근 통제가 아니다. 값은 여전히 브라우저까지 내려온다.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  maskPii,
  piiKindForFieldKey,
  isImagePii,
  type PiiKind,
} from "../../shared/privacy-mask";
import { DEFAULT_PRIVACY_IDLE_MINUTES } from "../../shared/privacy-lock";
import { fetchPrivacyMaskSettings } from "../api/privacyMask";
import { useToast } from "../components/common/Toast";

/**
 * 가려져 있는 동안 막히는 동작에 띄우는 말. **"암호를 넣어 주세요"가 아니다** —
 * 점검 나온 사람 눈에 잠가 둔 티가 나면 안 되고, 아직 안 만든 기능처럼 보여야 한다는 것이
 * 이 기능 요구의 핵심이다(티켓 #461).
 */
export const MASKED_ACTION_MESSAGE = "준비중입니다";

interface PrivacyMaskContextValue {
  /** 지금 가리는 중인가. */
  enabled: boolean;
  /** 암호가 맞아 '가리기 해제' 버튼이 떠 있는 상태(아직 열리진 않았다). */
  armed: boolean;
  /** 안 가려진 이유가 '암호로 열어서'인가(= 평소 상태가 아니라 잠깐 연 세션인가). */
  unlockedByPassword: boolean;
  /** 매장이 해제 암호를 정해 뒀는가. */
  hasPassword: boolean;
  /** 손 떼고 다시 가려지기까지(분). */
  idleMinutes: number;
  /** 검색창이 암호를 확인했을 때 — 버튼을 띄운다. */
  arm: () => void;
  /** '가리기 해제' 버튼 — 실제로 원본을 보여준다. */
  unmask: () => void;
  /** '다시 가리기' 버튼 — 즉시 도로 덮는다. */
  remask: () => void;
}

const PrivacyMaskContext = createContext<PrivacyMaskContextValue | undefined>(
  undefined,
);

/** 활동으로 칠 이벤트 — 이 중 하나라도 오면 "아직 사람이 앞에 있다"로 본다. */
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "wheel",
  "touchstart",
  "scroll",
] as const;

export function PrivacyMaskProvider({ children }: { children: ReactNode }) {
  // 시작은 **안 가린 상태**다(2026-08-14 뒤집힘 — 그전에는 언제나 가린 채로 시작했다).
  // 가리기를 당분간 쓰지 않기로 했고, 늘 가려져 있으면 사진 인쇄·문자·서식 출력 같은
  // 평소 업무가 죄다 `준비중입니다` 로 막혀 기능이 없는 것처럼 보였다(티켓 #459 반려).
  // 필요할 때만 상단 `가리기` 버튼으로 켜고, 푸는 건 종전대로 매장 암호다.
  const [enabled, setEnabled] = useState(false);
  const [armed, setArmed] = useState(false);
  // 암호를 넣어 연 세션인가 — 자동 잠금이 이때만 돈다(아래 useEffect 주석).
  const [unlockedByPassword, setUnlockedByPassword] = useState(false);

  // 매장 설정(암호 정해졌나 · 자동 잠금 분). 거의 안 바뀌는 값이라 오래 들고 쓴다 —
  // 화면마다 다시 물으면 저녁 피크에 쓸데없는 요청이 는다.
  const { data: settings } = useQuery({
    queryKey: ["privacy-mask-settings"],
    queryFn: fetchPrivacyMaskSettings,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
  });

  const idleMinutes = settings?.idleMinutes ?? DEFAULT_PRIVACY_IDLE_MINUTES;
  const hasPassword = settings?.hasPassword ?? false;

  // 매장 소속이 아닌 본사 관리자에게는 가리기를 걸지 않는다 — 가릴 고객 데이터도, 풀
  // 매장 암호도 없어서 걸어두면 아무 방법 없이 가려진 화면에 갇힌다.
  useEffect(() => {
    if (settings && settings.applies === false) setEnabled(false);
  }, [settings]);

  const arm = useCallback(() => setArmed(true), []);
  const unmask = useCallback(() => {
    setEnabled(false);
    setArmed(false);
    setUnlockedByPassword(true);
  }, []);
  const remask = useCallback(() => {
    setEnabled(true);
    setArmed(false);
    setUnlockedByPassword(false);
  }, []);

  // 암호로 연 세션에서만 도는 자동 잠금. 사람이 손을 대면 시계를 처음으로 되돌리고, 정해진
  // 시간(기본 1분) 동안 아무 일도 없으면 스스로 다시 덮는다.
  //
  // ⚠️ **`enabled === false` 만 보고 돌리면 안 된다.** 이제 평소가 안 가린 상태라, 그러면
  //    가리기를 켠 적도 없는 사람이 로그인하고 1분 가만히 있다가 갑자기 가려진 화면을
  //    만난다(안 쓰기로 한 기능이 스스로 켜지는 셈이다).
  useEffect(() => {
    if (enabled || !unlockedByPassword) return;
    let timer: ReturnType<typeof setTimeout>;
    const arm_ = () => {
      clearTimeout(timer);
      timer = setTimeout(() => remask(), Math.max(1, idleMinutes) * 60_000);
    };
    // 마우스가 움직일 때마다 타이머를 새로 거는 건 낭비라, 1초에 한 번만 되감는다.
    let lastReset = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastReset < 1000) return;
      lastReset = now;
      arm_();
    };
    arm_();
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    return () => {
      clearTimeout(timer);
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
    };
  }, [enabled, unlockedByPassword, idleMinutes, remask]);

  const value = useMemo(
    () => ({
      enabled,
      armed,
      unlockedByPassword,
      hasPassword,
      idleMinutes,
      arm,
      unmask,
      remask,
    }),
    [
      enabled,
      armed,
      unlockedByPassword,
      hasPassword,
      idleMinutes,
      arm,
      unmask,
      remask,
    ],
  );

  return (
    <PrivacyMaskContext.Provider value={value}>
      {children}
    </PrivacyMaskContext.Provider>
  );
}

/**
 * 모드 상태·해제 조작. Provider 밖에서 불려도 죽지 않고 "가리지 않음"으로 답한다 —
 * 공개 입력폼(/form/:token)처럼 고객 본인이 자기 정보를 넣는 화면은 이 Provider 밖에
 * 있고, 그 화면까지 가려버리면 고객이 자기가 뭘 썼는지 못 본다.
 */
export function usePrivacyMask(): PrivacyMaskContextValue {
  const ctx = useContext(PrivacyMaskContext);
  if (!ctx) {
    return {
      enabled: false,
      armed: false,
      unlockedByPassword: false,
      hasPassword: false,
      idleMinutes: DEFAULT_PRIVACY_IDLE_MINUTES,
      arm: () => {},
      unmask: () => {},
      remask: () => {},
    };
  }
  return ctx;
}

/** 지금 가리는 중인지만 필요할 때. */
export function useIsMasking(): boolean {
  return usePrivacyMask().enabled;
}

/**
 * 가려져 있는 동안 막히는 동작(문자 발송·전화 걸기·인쇄·서식 출력·엑셀 내보내기)의 문지기.
 *
 * ```ts
 * const blocked = useMaskedActionGuard();
 * if (blocked()) return;   // 가려져 있으면 '준비중입니다' 를 띄우고 그대로 끝낸다
 * ```
 */
export function useMaskedActionGuard(): () => boolean {
  const masking = useIsMasking();
  const toast = useToast();
  const maskingRef = useRef(masking);
  maskingRef.current = masking;
  return useCallback(() => {
    if (!maskingRef.current) return false;
    toast.info(MASKED_ACTION_MESSAGE);
    return true;
  }, [toast]);
}

/**
 * 빌트인 항목 키를 아는 화면용 — 값 하나를 상황에 맞게 가려서 돌려준다.
 * 가릴 항목이 아니거나 모드가 꺼져 있으면 원본 그대로.
 */
export function useMaskedFieldValue(key: string, display: string): string {
  const enabled = useIsMasking();
  if (!enabled) return display;
  const kind = piiKindForFieldKey(key);
  if (!kind || isImagePii(kind)) return display;
  return maskPii(display, kind);
}

/** 항목 키 없이 "이건 이름/전화다"를 아는 화면용(목록의 고객명 열 등). */
export function useMaskedValue(value: string, kind: PiiKind): string {
  const enabled = useIsMasking();
  if (!enabled || isImagePii(kind)) return value;
  return maskPii(value, kind);
}

/**
 * 값 하나를 가려 그리는 최소 컴포넌트. 훅을 쓸 수 없는 자리(반복문 안에서 조건부로
 * 부르게 되는 자리)에서 쓴다.
 */
export function MaskedText({
  value,
  kind,
  fallback = "",
}: {
  value: string | null | undefined;
  kind: PiiKind;
  fallback?: string;
}) {
  const enabled = useIsMasking();
  const raw = value ?? "";
  if (raw === "") return <>{fallback}</>;
  if (!enabled || isImagePii(kind)) return <>{raw}</>;
  return <>{maskPii(raw, kind)}</>;
}

/**
 * 신분증·카드 사진·서명처럼 **글자로는 못 가리는 이미지**를 흐리게 덮는다.
 * 가려져 있으면 blur, 풀면 그대로 선명해진다. `<img>` 를 그대로 두고 클래스만 얹는 이유는
 * 값(주소)을 감추는 게 목적이 아니라 **어깨너머로 안 읽히게** 하는 게 목적이라서다.
 */
export function piiImageClassName(masking: boolean, extra = ""): string {
  return `${extra} ${masking ? "blur-lg" : ""}`.trim();
}

/**
 * 개인정보가 담긴 사진 한 장. `<img>` 대신 이걸 쓰면 가려져 있는 동안 자동으로 흐려진다.
 *
 * 컴포넌트로 둔 이유는 **조건부로 그려지는 자리**(미리보기 창은 열렸을 때만 그린다)에서도
 * 안전하기 때문이다 — 훅을 그런 자리에서 부르면 훅 순서가 어긋난다.
 */
export function PiiImage({
  className = "",
  ...rest
}: React.ImgHTMLAttributes<HTMLImageElement>) {
  const masking = useIsMasking();
  return <img {...rest} className={piiImageClassName(masking, className)} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// 고치는 자리(입력칸)를 가릴 때 — **가림과 잠금은 한 쌍이다** (티켓 #480)
// ─────────────────────────────────────────────────────────────────────────────
//
// 그전까지 편집 입력칸은 **일부러** 안 가렸다. 별표를 씌운 글자가 그대로 저장되면 실제
// 고객 정보가 지워지기 때문이다(자동 저장이 도는 창이라 손도 안 대고 날아간다).
// 그런데 그 때문에 신청서 한 장만 열면 그 고객의 정보가 통째로 드러나 가려 둔 의미가
// 없었다. 그래서 **가리는 순간 그 칸을 아예 못 고치게 잠근다** — 못 고치니 가린 글자가
// 저장될 길이 없고, 번호를 고쳐야 하면 상단에서 가리기를 풀면 된다.
//
// ⚠️ 아래 두 컴포넌트는 **값을 바꾸는 통로를 아예 갖고 있지 않다**(onChange 자체가 없다).
//    "읽기 전용으로 보이게" 가 아니라 "바꿀 수단이 없게" 로 만든 것이 안전장치다.

/** 잠긴 칸에 마우스를 올렸을 때 뜨는 말. 요청자가 지정한 문장 그대로 쓴다(티켓 #480). */
export const MASKED_FIELD_LOCK_TITLE = "수정할 수 없습니다";

/** 잠긴 입력칸의 모양 — 회색 판에 별표만 얹혀 "지금은 볼 수도 고칠 수도 없는 칸"으로 읽힌다. */
const LOCKED_INPUT_BOX_CLS =
  "h-8 w-full rounded-md border border-gray-200 bg-gray-100 px-2 text-sm " +
  "dark:border-gray-700 dark:bg-white/[0.06]";
/** 모양과 무관하게 항상 붙는 잠금 표시 — 클릭도 선택도 안 되게 한다. */
const LOCKED_INPUT_LOCK_CLS =
  "cursor-not-allowed select-none pointer-events-none text-gray-500 dark:text-gray-400";

/**
 * 글자로 된 개인정보 칸(이름·전화·주민번호·카드/계좌번호…)을 별표로 덮고 잠근다.
 *
 * 값이 비어 있어도 똑같이 잠근다 — "빈 칸은 열어 준다"로 가르면 같은 화면에서 어떤 칸은
 * 고쳐지고 어떤 칸은 안 고쳐져, 왜 그런지 화면만 봐선 알 수 없게 된다(티켓 #480 지정).
 */
export function MaskedLockedInput({
  value,
  kind,
  className = "",
  inputClassName = LOCKED_INPUT_BOX_CLS,
}: {
  value: string | null | undefined;
  kind: PiiKind;
  className?: string;
  /** 칸 크기가 화면마다 달라서(신청서는 한 줄, 입력폼은 큰 칸) 겉모양만 바꿔 끼운다. */
  inputClassName?: string;
}) {
  const raw = value ?? "";
  return (
    <div
      title={MASKED_FIELD_LOCK_TITLE}
      data-pii-locked="true"
      className={`cursor-not-allowed ${className}`}
    >
      <input
        type="text"
        readOnly
        tabIndex={-1}
        aria-readonly="true"
        aria-label={MASKED_FIELD_LOCK_TITLE}
        value={maskPii(raw, kind)}
        className={`${inputClassName} ${LOCKED_INPUT_LOCK_CLS}`}
      />
    </div>
  );
}

/**
 * 글자로는 못 가리는 칸(자필 서명·신분증/카드 사진)을 흐리게 덮고 손도 못 대게 막는다.
 * 원래 컨트롤을 그대로 감싸므로 가리기를 풀면 즉시 원래 화면·원래 동작으로 돌아온다.
 */
export function MaskedLockedArea({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      title={MASKED_FIELD_LOCK_TITLE}
      data-pii-locked="true"
      className={`cursor-not-allowed ${className}`}
    >
      <div className="pointer-events-none select-none blur-md">{children}</div>
    </div>
  );
}
